/**
 * GenerationAddEquipmentDialog — user-supplied equipment entry form.
 *
 * Entries created here are UNVERIFIED: they never claim an OEM basis and are
 * screened through the same solver normalization as the sourced catalog. The
 * live preview shows the exact solver duty the entry would produce so the
 * net→gross and LHV→HHV normalization is never a surprise.
 */
import React, { useMemo, useState } from 'react';
import { DatabaseZap, PlusCircle, X } from 'lucide-react';

import type {
  GenerationEquipmentRatingScope,
  GenerationUserEquipmentEntry,
} from '../../../types/generationImpact';
import {
  normalizeGenerationUserEquipmentDuty,
  validateGenerationUserEquipmentEntry,
} from '../../../lib/generationImpact/userEquipmentEnvelope';
import type { GenerationUserEquipmentPrefill } from '../../../lib/generationImpact/equipmentImportSources';

type UserEquipmentTechnology = GenerationUserEquipmentEntry['technology'];

export interface GenerationAddEquipmentDialogProps {
  open: boolean;
  /**
   * Optional import prefill (BOM line / OEM database / catalog entry). The
   * parent remounts the dialog (key) per import, so lazy state init suffices
   * and the blank flow stays byte-identical.
   */
  prefill?: GenerationUserEquipmentPrefill;
  onClose: () => void;
  onAdd: (entry: GenerationUserEquipmentEntry) => void;
}

const technologyOptions: Array<[UserEquipmentTechnology, string]> = [
  ['combined-cycle-block', 'Combined-cycle block'],
  ['simple-cycle-gas-turbine', 'Simple-cycle gas turbine'],
  ['reciprocating-engine', 'Reciprocating engines'],
];

const ratingScopeOptions: Array<[GenerationEquipmentRatingScope, string]> = [
  ['net-plant', 'Net plant'],
  ['gross-plant', 'Gross plant'],
  ['generator-terminals', 'Generator terminals'],
];

function newEntryId(): string {
  return `user-equipment-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const fieldLabelClass = 'block text-[9px] font-bold uppercase tracking-wider text-text-muted';
const fieldInputClass = 'mt-1 w-full border border-border bg-background px-2 py-1.5 text-xs text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary';

export function GenerationAddEquipmentDialog({
  open,
  prefill,
  onClose,
  onAdd,
}: GenerationAddEquipmentDialogProps): React.JSX.Element | null {
  const [manufacturer, setManufacturer] = useState(prefill?.manufacturer ?? '');
  const [model, setModel] = useState(prefill?.model ?? '');
  const [trainConfiguration, setTrainConfiguration] = useState(prefill?.trainConfiguration ?? '');
  const [technology, setTechnology] = useState<UserEquipmentTechnology>(prefill?.technology ?? 'combined-cycle-block');
  const [unitsPerBlock, setUnitsPerBlock] = useState(String(prefill?.unitsPerBlock ?? 1));
  const [ratedOutput, setRatedOutput] = useState(
    prefill?.ratedOutputMWPerBlock != null ? String(prefill.ratedOutputMWPerBlock) : '',
  );
  const [ratingScope, setRatingScope] = useState<GenerationEquipmentRatingScope>(prefill?.ratingScope ?? 'net-plant');
  const [heatRateValue, setHeatRateValue] = useState(
    prefill?.heatRateValueKjPerKwh != null
      ? String(Math.round(prefill.heatRateValueKjPerKwh))
      : '',
  );
  const [heatRateUnit, setHeatRateUnit] = useState<'kJ/kWh' | 'Btu/kWh'>('kJ/kWh');
  // '' = the import's heating-value basis is not published — validation blocks
  // submit until the user chooses from the datasheet.
  const [heatingValueBasis, setHeatingValueBasis] = useState<'' | 'LHV' | 'HHV'>(
    prefill && prefill.heatRateValueKjPerKwh != null && prefill.heatingValueBasis == null
      ? ''
      : prefill?.heatingValueBasis ?? 'LHV',
  );
  const [auxiliaryPct, setAuxiliaryPct] = useState('4');
  const [ambientPct, setAmbientPct] = useState('90');
  const [degradationPct, setDegradationPct] = useState('98');
  const [oemContactEmail, setOemContactEmail] = useState('');
  const [notes, setNotes] = useState(prefill?.notes ?? '');
  const [verificationRequested, setVerificationRequested] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const prompts = prefill?.promptFields ?? [];

  const draft = useMemo((): GenerationUserEquipmentEntry => ({
    id: newEntryId(),
    manufacturer: manufacturer.trim(),
    model: model.trim(),
    trainConfiguration: trainConfiguration.trim() || `${technologyOptions.find(([id]) => id === technology)?.[1] ?? technology} · user-defined`,
    technology,
    unitsPerBlock: Number(unitsPerBlock),
    ratedOutputMWPerBlock: Number(ratedOutput),
    ratingScope,
    heatRateValue: heatRateValue === '' ? null : Number(heatRateValue),
    heatRateUnit: heatRateValue === '' ? null : heatRateUnit,
    heatingValueBasis: heatRateValue === '' || heatingValueBasis === '' ? null : heatingValueBasis,
    auxiliaryFractionOfIsoGross: Number(auxiliaryPct) / 100,
    ambientFactor: Number(ambientPct) / 100,
    degradationFactor: Number(degradationPct) / 100,
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    ...(oemContactEmail.trim() ? { oemContactEmail: oemContactEmail.trim() } : {}),
    ...(verificationRequested ? { verificationRequested } : {}),
    ...(prefill?.importSource ? { importSource: prefill.importSource } : {}),
    createdAt: new Date().toISOString(),
    // The id/createdAt above regenerate per keystroke; both are replaced with
    // stable values at submit time so the preview memo stays dependency-pure.
  }), [
    ambientPct, auxiliaryPct, degradationPct, heatRateUnit, heatRateValue,
    heatingValueBasis, manufacturer, model, notes, oemContactEmail, prefill,
    ratedOutput, ratingScope, technology, trainConfiguration, unitsPerBlock,
    verificationRequested,
  ]);

  const errors = useMemo(() => validateGenerationUserEquipmentEntry(draft), [draft]);
  const preview = useMemo(
    () => (errors.length === 0 ? normalizeGenerationUserEquipmentDuty(draft) : null),
    [draft, errors],
  );

  if (!open) return null;

  const submit = (): void => {
    setSubmitted(true);
    if (errors.length > 0) return;
    onAdd({ ...draft, id: newEntryId(), createdAt: new Date().toISOString() });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      data-testid="generation-add-equipment-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generation-add-equipment-title"
    >
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-border bg-card p-5 space-y-4 rounded-lg">
        <div className="flex items-center gap-2">
          <PlusCircle className="h-4 w-4 text-primary" aria-hidden />
          <h3 id="generation-add-equipment-title" className="flex-1 text-xs font-bold uppercase tracking-[0.2em] text-text-primary">
            Add equipment (user-supplied)
          </h3>
          <button
            type="button"
            aria-label="Close add equipment"
            className="text-text-muted hover:text-text-primary"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {prefill && (
          <p
            className="flex items-start gap-2 border border-primary/40 bg-primary/5 px-3 py-2 text-[10px] text-primary rounded-md"
            data-testid="generation-add-equipment-import-chip"
          >
            <DatabaseZap className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              <strong className="uppercase tracking-wider">Imported from {prefill.sourceLabel}</strong>
              {' '}— review every field; the entry stays user-supplied and unverified.
            </span>
          </p>
        )}

        <p className="text-[10px] leading-relaxed text-text-muted">
          User-supplied equipment screens through the same solver normalization as the sourced
          catalog but stays <strong className="text-warning">unverified</strong>: it carries no OEM
          source and is labelled as a user assumption everywhere it appears. Use the OEM data
          request to obtain the published basis, and nominate the entry for platform verification
          once vendor documents arrive.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={fieldLabelClass}>Manufacturer</span>
            <input className={fieldInputClass} value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder="e.g. Solar Turbines" />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>Model</span>
            <input className={fieldInputClass} value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. Titan 350" />
          </label>
          <label className="block sm:col-span-2">
            <span className={fieldLabelClass}>Train configuration</span>
            <input className={fieldInputClass} value={trainConfiguration} onChange={e => setTrainConfiguration(e.target.value)} placeholder="e.g. 2×1 combined-cycle block · 60 Hz" />
          </label>
          <div>
            <label className="block">
              <span className={fieldLabelClass}>Technology</span>
              <select className={fieldInputClass} value={technology} onChange={e => setTechnology(e.target.value as UserEquipmentTechnology)}>
                {technologyOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </label>
            {prompts.includes('technology') && (
              <span className="mt-1 block text-[9px] normal-case text-warning">
                The source does not determine the screening technology — confirm the archetype.
              </span>
            )}
          </div>
          <label className="block">
            <span className={fieldLabelClass}>Engines / turbines per block</span>
            <input className={fieldInputClass} inputMode="numeric" value={unitsPerBlock} onChange={e => setUnitsPerBlock(e.target.value)} />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>Rated output (MW per block)</span>
            <input className={fieldInputClass} inputMode="decimal" value={ratedOutput} onChange={e => setRatedOutput(e.target.value)} placeholder="e.g. 438" />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>Rating scope</span>
            <select className={fieldInputClass} value={ratingScope} onChange={e => setRatingScope(e.target.value as GenerationEquipmentRatingScope)}>
              {ratingScopeOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <div>
            <label className="block">
              <span className={fieldLabelClass}>Heat rate</span>
              <input className={fieldInputClass} inputMode="decimal" value={heatRateValue} onChange={e => setHeatRateValue(e.target.value)} placeholder="e.g. 5783" />
            </label>
            {prefill?.heatRateDerived && (
              <span className="mt-1 block text-[9px] normal-case text-warning">
                Derived from published electrical efficiency — not an OEM heat-rate guarantee.
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={fieldLabelClass}>Unit</span>
              <select className={fieldInputClass} value={heatRateUnit} onChange={e => setHeatRateUnit(e.target.value as 'kJ/kWh' | 'Btu/kWh')}>
                <option value="kJ/kWh">kJ/kWh</option>
                <option value="Btu/kWh">Btu/kWh</option>
              </select>
            </label>
            <div>
              <label className="block">
                <span className={fieldLabelClass}>Basis</span>
                <select className={fieldInputClass} value={heatingValueBasis} onChange={e => setHeatingValueBasis(e.target.value as '' | 'LHV' | 'HHV')}>
                  <option value="" disabled>Select basis…</option>
                  <option value="LHV">LHV</option>
                  <option value="HHV">HHV</option>
                </select>
              </label>
              {prompts.includes('heatingValueBasis') && heatingValueBasis === '' && (
                <span className="mt-1 block text-[9px] normal-case text-warning">
                  The OEM source does not state LHV or HHV — confirm from the datasheet before screening.
                </span>
              )}
            </div>
          </div>
          <label className="block">
            <span className={fieldLabelClass}>Auxiliary load (% of ISO gross)</span>
            <input className={fieldInputClass} inputMode="decimal" value={auxiliaryPct} onChange={e => setAuxiliaryPct(e.target.value)} />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>Ambient output factor (%)</span>
            <input className={fieldInputClass} inputMode="decimal" value={ambientPct} onChange={e => setAmbientPct(e.target.value)} />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>Degradation factor (%)</span>
            <input className={fieldInputClass} inputMode="decimal" value={degradationPct} onChange={e => setDegradationPct(e.target.value)} />
          </label>
          <label className="block">
            <span className={fieldLabelClass}>OEM contact email (optional)</span>
            <input className={fieldInputClass} inputMode="email" value={oemContactEmail} onChange={e => setOemContactEmail(e.target.value)} placeholder="sales@oem.example" />
          </label>
          <label className="block sm:col-span-2">
            <span className={fieldLabelClass}>Notes (optional)</span>
            <input className={fieldInputClass} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Source of these numbers, quote reference, caveats…" />
          </label>
        </div>

        <label className="flex items-start gap-2 text-[10px] text-text-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={verificationRequested}
            onChange={e => setVerificationRequested(e.target.checked)}
          />
          <span>
            Nominate for platform verification — flags this entry for engineering source review so
            it can graduate into the sourced catalog once OEM documents are on record.
          </span>
        </label>

        {preview && (
          <div className="border border-border bg-background px-3 py-2 text-[10px] rounded-md" data-testid="generation-add-equipment-preview">
            <span className="font-bold uppercase tracking-wider text-text-muted">Solver duty preview</span>
            <p className="mt-1 text-text-primary">
              ISO gross {preview.solverIsoGrossMWPerBlock.toFixed(1)} MW / block ·
              {' '}{Math.round(preview.solverHeatRateBtuPerKWhHhv).toLocaleString('en-US')} Btu/kWh HHV on gross output
            </p>
            <p className="mt-1 text-text-muted">{preview.normalizationNote}</p>
          </div>
        )}

        {submitted && errors.length > 0 && (
          <ul className="list-disc border border-danger/50 bg-danger/10 px-4 py-2 pl-7 text-[10px] text-danger rounded-md" role="alert">
            {errors.map(error => <li key={error}>{error}</li>)}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:bg-background rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted rounded-md"
            disabled={submitted && errors.length > 0}
            onClick={submit}
          >
            <PlusCircle className="h-3 w-3" /> Add to equipment set
          </button>
        </div>
      </div>
    </div>
  );
}
